import * as React from 'react'
import { UsersThree, CircleDashed } from '@phosphor-icons/react'
import { Loader } from 'src/design-system'

interface Segment {
  labelSlug: string
  labelName: string
  color: string
  percentage: string
  count: number
}

interface ChartProps {
  segments: Segment[]
  total: number
  loading?: boolean
  skillGapData?: {
    skillGapPercentage: string
    requireLevel: string
    assessedLevel: string
    assessedSkillGap: number
    individuals?: Array<{
      name: string
      requiredLevel: string
      assessedLevel: string
      skillGap: string
    }>
  } | null
}

export const Chart: React.FC<ChartProps> = ({
  segments,
  total,
  loading = false,
  skillGapData,
}) => {
  return (
    <div className="flex flex-col gap-6">
      {loading && (
        <div className="flex items-center justify-center py-8">
          <div className="flex flex-col items-center gap-3">
            <Loader className="w-8 h-8" />
            <span className="text-xs text-gray-500">
              This might take a few seconds
            </span>
          </div>
        </div>
      )}

      {!loading && (
        <>
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-start gap-3">
              <h4 className="font-semibold text-gray-900">People Using</h4>
              <span className="text-base text-gray-900 flex items-center gap-1">
                <UsersThree size={18} className="text-gray-500" />
                {total}
              </span>
            </div>

            <div className="flex items-start gap-6">
              <div className="relative w-24 h-24">
                <svg
                  className="w-24 h-24 transform -rotate-90"
                  viewBox="0 0 100 100"
                >
                  <circle
                    cx="50"
                    cy="50"
                    r="35"
                    fill="none"
                    stroke="#e5e7eb"
                    strokeWidth="16"
                  />
                  {segments.map((segment, index) => {
                    const percentage = parseInt(segment.percentage)
                    const previousPercentages = segments
                      .slice(0, index)
                      .reduce(
                        (sum, prev) => sum + parseInt(prev.percentage),
                        0
                      )

                    const strokeDasharray = `${
                      2 * Math.PI * 35 * (percentage / 100)
                    } ${2 * Math.PI * 35}`
                    const strokeDashoffset = `-${
                      2 * Math.PI * 35 * (previousPercentages / 100)
                    }`

                    return (
                      <circle
                        key={segment.labelSlug}
                        cx="50"
                        cy="50"
                        r="35"
                        fill="none"
                        stroke={segment.color}
                        strokeWidth="16"
                        strokeDasharray={strokeDasharray}
                        strokeDashoffset={strokeDashoffset}
                      />
                    )
                  })}
                </svg>
              </div>

              <div className="flex flex-col gap-3">
                {segments.map((segment) => (
                  <div key={segment.labelSlug} className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: segment.color }}
                    />
                    <span className="text-sm text-gray-700">
                      {segment.labelName}: {segment.percentage}, {segment.count} people
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold text-[#686464]">
                Skill Gap
              </h4>
              <span className="px-10 py-0.4 bg-[#A5E3BA] text-[#30592c] text-sm font-medium rounded-full">
                {skillGapData?.skillGapPercentage}
              </span>
            </div>

            <div className="flex flex-col gap-4">
              <div className="flex justify-between items-center">
                <span className="text-sm font-semibold text-[#686464]">
                  Require Level
                </span>
                <span className="text-sm text-gray-900">
                  {skillGapData?.requireLevel}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-semibold text-[#686464]">
                  Assessed Level
                </span>
                <span className="text-sm text-gray-900">
                  {skillGapData?.assessedLevel}
                </span>
              </div>
            </div>

            <div className="h-px bg-gray-100"></div>

            {total > 0 &&
            skillGapData?.individuals &&
            skillGapData.individuals.length > 0 ? (
              <div className="flex flex-col">
                <div className="flex justify-between text-xs font-medium text-[#686464] mb-3">
                  <div className="text-left flex-1">Name</div>
                  <div className="text-right w-20">Required Lv.</div>
                  <div className="text-right w-20">Assessed Lv.</div>
                  <div className="text-right w-20">Skill Gap</div>
                </div>

                <div className="flex flex-col gap-2">
                  {skillGapData.individuals.map((person, index) => (
                    <div key={index} className="flex justify-between text-xs">
                      <div className="text-left text-gray-900 flex-1">
                        {person.name}
                      </div>
                      <div className="text-right text-gray-900 w-20">
                        {person.requiredLevel}
                      </div>
                      <div className="text-right text-gray-900 w-20">
                        {person.assessedLevel}
                      </div>
                      <div className="text-right text-green-800 w-20">
                        {person.skillGap}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8">
                <div className="flex flex-col items-center gap-3">
                  <CircleDashed className="w-8 h-8 text-gray-400" />
                  <span className="text-sm text-gray-500">No users found</span>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
